// @flow

import React from 'react';
import styled from 'styled-components';
import { defaultTheme as themes } from '../themes';
import {
  CloseIcon,
  CheckmarkIcon,
  ErrorIcon,
  InformationIcon,
  WarningIcon,
} from './Icons';
import { HSpacer } from './Layout';

const toastMessageTypes = {
  INFO: 'INFO',
  WARNING: 'WARNING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
};

type Props = {
  text: string,
  open: boolean,
  onClick?: Function,
  showClose?: boolean,
  showIcon?: boolean,
  type?: $Values<typeof toastMessageTypes>,
};

function ToastMessage(props: Props) {
  function getIcon(type) {
    switch (type) {
      case toastMessageTypes.WARNING:
        return <WarningIcon />;
      case toastMessageTypes.SUCCESS:
        return <CheckmarkIcon />;
      case toastMessageTypes.ERROR:
        return <ErrorIcon />;
      case toastMessageTypes.INFO:
      default:
        return <InformationIcon />;
    }
  }

  function getColors(type) {
    switch (type) {
      case toastMessageTypes.WARNING:
        return {
          color: themes.primary.blue[0],
          backgroundColor: themes.secondary.yellow[0],
        };
      case toastMessageTypes.SUCCESS:
        return {
          color: themes.primary.white[0],
          backgroundColor: themes.secondary.green[0],
        };
      case toastMessageTypes.ERROR:
        return {
          color: themes.primary.white[0],
          backgroundColor: themes.secondary.red[0],
        };
      case toastMessageTypes.INFO:
        return {
          color: themes.primary.white[0],
          backgroundColor: themes.secondary.lightBlue[0],
        };
      default:
        return {
          color: themes.primary.white[0],
          backgroundColor: themes.primary.blue[0],
        };
    }
  }

  function onClick() {
    props.onClick && props.onClick();
  }

  const colors = getColors(props.type);

  return props.open ? (
    <Container
      onClick={onClick}
      color={colors.color}
      showClose={props.showClose}
      backgroundColor={colors.backgroundColor}
    >
      {props.showIcon ? <HSpacer small={1} large={2} /> : null}
      {props.showIcon ? getIcon(props.type) : null}
      <HSpacer small={1} large={2} />
      <ToastText>{props.text}</ToastText>
      {props.showClose ? <HSpacer small={1} large={2} /> : null}
      {props.showClose ? <CloseIcon /> : null}
      <HSpacer small={1} large={2} />
    </Container>
  ) : null;
}

ToastMessage.defaultProps = { showClose: true, showIcon: true };

const ToastText = styled.span`
  flex-grow: 1;
`;

const Container = styled.div`
  ${(props) => (props.showClose ? 'cursor: pointer;' : null)} display: flex;
  width: 100%;
  min-height: 60px;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.backgroundColor};
  color: ${(props) => props.color};
`;

export const DefaultToastMessage = (props: Props) => (
  <ToastMessage {...props} />
);
export const ErrorToastMessage = (props: Props) => (
  <ToastMessage {...props} type={toastMessageTypes.ERROR} />
);
export const InfoToastMessage = (props: Props) => (
  <ToastMessage {...props} type={toastMessageTypes.INFO} />
);
export const WarningToastMessage = (props: Props) => (
  <ToastMessage {...props} type={toastMessageTypes.WARNING} />
);
export const SuccessToastMessage = (props: Props) => (
  <ToastMessage {...props} type={toastMessageTypes.SUCCESS} />
);
